import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

interface ConsentModalProps {
  open: boolean;
  onAccept: () => void;
}

export function ConsentModal({ open, onAccept }: ConsentModalProps) {
  const navigate = useNavigate();
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);

  const canAccept = acceptedTerms && acceptedPrivacy;

  const handleAccept = () => {
    if (canAccept) {
      onAccept();
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Consentement et données personnelles</DialogTitle>
          <DialogDescription>
            Avant de créer ton compte, nous avons besoin de ton consentement pour traiter tes données.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-6">
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <Checkbox
                id="terms"
                checked={acceptedTerms}
                onCheckedChange={(checked) => setAcceptedTerms(checked as boolean)}
              />
              <div className="flex-1">
                <Label htmlFor="terms" className="cursor-pointer font-medium">
                  J'accepte les Conditions Générales d'Utilisation
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Tu peux les consulter{" "}
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 h-auto"
                    onClick={() => window.open("/legal/terms", "_blank")}
                  >
                    ici
                  </Button>
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                id="privacy"
                checked={acceptedPrivacy}
                onCheckedChange={(checked) => setAcceptedPrivacy(checked as boolean)}
              />
              <div className="flex-1">
                <Label htmlFor="privacy" className="cursor-pointer font-medium">
                  J'accepte la Politique de Confidentialité
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Tu peux la consulter{" "}
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 h-auto"
                    onClick={() => window.open("/legal/privacy", "_blank")}
                  >
                    ici
                  </Button>
                </p>
              </div>
            </div>
          </div>

          <div className="bg-secondary/50 p-4 rounded-lg space-y-2">
            <h4 className="font-semibold text-sm">Tes droits RGPD :</h4>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>✓ Droit d'accès et de rectification de tes données</li>
              <li>✓ Droit à l'effacement (droit à l'oubli)</li>
              <li>✓ Droit à la portabilité de tes données</li>
              <li>✓ Droit d'opposition au traitement</li>
            </ul>
          </div>
        </DialogBody>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => navigate("/")}>
            Annuler
          </Button>
          <Button onClick={handleAccept} disabled={!canAccept}>
            Accepter et continuer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
